// Single source of truth for services. Used by:
//   - Services grid (landing page)
//   - ServiceDetail page (/services/:slug)
//   - Quote form site-type dropdown
export const SERVICES = [
  {
    slug: "commercial-office",
    tag: "01 / Offices",
    title: "Corporate & Commercial Office Cleaning",
    body: "Tailored facilities cleaning engineered to maintain hygienic, productive, and prestigious corporate environments.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/42oqsa0a_sfsdf.png",
    intro:
      "At AGS Cleaning Services, we deliver professional commercial cleaning for boutique coworking hubs, multi-floor corporate headquarters, and landmark business centres across Ireland. We understand that a clean office directly impacts your brand reputation and employee well-being. Our bespoke cleaning schedules are seamlessly integrated into your facility's operations — providing consistently immaculate workspaces without ever disrupting your day-to-day productivity.",
    features: [
      "Bespoke Scheduling — daily, weekly, or out-of-hours programmes",
      "High-Efficiency Workstation & Touchpoint Sanitisation",
      "Floor Care & Architectural Glass Maintenance",
      "Kitchen & Restroom Hygiene Management",
      "Scheduled Deep-Clean & Periodic Cycles",
      "Available as One-Off cleans or recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "From boutique coworking to multi-floor HQs, our office programmes are modular — pick a recurring schedule, a one-off deep clean, or a hybrid.",
        items: [
          {
            k: "Bespoke Scheduling & Out-of-Hours Care",
            v: "Fully customisable daily, weekly, or discrete out-of-hours cleaning programmes (early morning, evening, or weekends) matched to your operational footprint.",
          },
          {
            k: "High-Efficiency Workstation & Touchpoint Sanitisation",
            v: "Deep sanitisation of desks, tech equipment, meeting room tables, and high-frequency touchpoints (handles, lift buttons) helping maintain a cleaner and healthier workplace.",
          },
          {
            k: "Floor Care & Architectural Glass Maintenance",
            v: "Precision carpet vacuuming, specialist hard-floor care, and crystal-clear cleaning of internal glass partitions and entrance doors.",
          },
          {
            k: "Kitchen & Restroom Hygiene Management",
            v: "Rigorous cleaning and disinfection of breakout areas, paired with proactive washroom hygiene management and automated consumables replenishment.",
          },
          {
            k: "Scheduled Deep-Clean & Periodic Cycles",
            v: "Strategic quarterly deep cleans, upholstery washing, and carpet hot-water extraction scheduled in advance to protect your facility investments.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "Whether you need a single end-of-tenancy deep clean or an ongoing daily programme, we'll match the schedule to your premises and people.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Move-in / move-out deep cleans, post-event recovery cleans, pre-inspection sparkle cleans — booked once, delivered to spec.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, weekly, fortnightly, or monthly cleaning programmes with a dedicated crew, fixed schedule, and consistent quality KPIs.",
          },
          {
            k: "Hybrid Programmes",
            v: "Combine a recurring base schedule with quarterly deep-clean cycles, periodic carpet extraction, and on-demand event support.",
          },
        ],
      },
      {
        heading: "Why Corporate Facility Managers Trust Us",
        body: "We work closely with your facilities team to deliver reliable cleaning services with minimal disruption to your daily operations.",
        items: [
          {
            k: "Consistent & Audited Quality Standards",
            v: "Our cleaning crews follow strict KPIs and Service Level Agreements (SLAs) with regular management audits to ensure consistent quality standards.",
          },
          {
            k: "Eco-Friendly & Sustainable Practices",
            v: "We use EU Ecolabel certified, non-toxic cleaning agents and sustainable processes to align with your corporate CSR targets.",
          },
          {
            k: "Fully Vetted & Professional Personnel",
            v: "Every operative is professionally trained, uniformed, and committed to maintaining high standards of workplace professionalism and confidentiality.",
          },
          {
            k: "Fully Insured for High-Value Assets",
            v: "Backed by comprehensive Public and Employers' Liability Insurance, giving you total peace of mind for high-end office tech and fit-outs.",
          },
        ],
      },
    ],
  },
  {
    slug: "industrial",
    tag: "02 / Industrial",
    title: "Heavy Industrial & Manufacturing Plant Cleaning",
    body: "High-standard technical cleaning engineered to help maintain equipment performance and cleanliness, plant productivity, and support safe working environments.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/jyzviepf_689a33f09412eac56cc83436d5cd1a53.jpg",
    intro:
      "Industrial and manufacturing environments demand heavy-duty, highly specialised cleaning solutions that protect both your asset investments and your workforce. At AGS Cleaning Services, we deliver technical industrial cleaning tailored for heavy production halls, processing plants, and high-capacity manufacturing suites. Our industrial crews are trained in safe working practices, RAMS-compliant, and trained to navigate hazardous or complex environments — designing workflows around your production schedules to help minimise disruption to production, and working in line with site safety requirements.",
    features: [
      "Heavy Machinery, Tooling & Equipment Degreasing",
      "High-Level Structural Dusting, Gantries & Ducts",
      "Industrial Floor Scrubbing & Line-Marking Refresh",
      "Safe Chemical Handling & SDS Management",
      "Strategic Factory Shutdowns & Emergency Response",
      "Available as One-Off shutdown cleans or recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is engineered around your machinery, your shutdown windows, and your HSA inspection cadence.",
        items: [
          {
            k: "Heavy Machinery, Tooling & Equipment Degreasing",
            v: "Precision deep-cleaning and high-grade degreasing of production lines, heavy plant machinery, and hydraulic components to ensure equipment longevity and prevent mechanical failure.",
          },
          {
            k: "High-Level Structural Dusting, Gantries & Ducts",
            v: "Safe, high-level extraction of industrial dust and combustible materials from overhead gantries, ventilation ductwork, support beams, and high-bay lighting systems.",
          },
          {
            k: "Industrial Floor Scrubbing & Line-Marking Refresh",
            v: "Mechanical floor scrubbing using industrial-grade equipment to eliminate stubborn oil, chemical, and tyre stains — paired with safety line-marking restoration to maintain safe walkways.",
          },
          {
            k: "Safe Chemical Handling & SDS Management",
            v: "Strict execution of all chemical applications in full compliance with COSHH (Control of Substances Hazardous to Health) regulations, backed by meticulous on-site Safety Data Sheet (SDS) records.",
          },
          {
            k: "Strategic Factory Shutdowns & Emergency Response",
            v: "Highly coordinated cleaning teams deployed during planned annual facility shutdowns, preventive maintenance windows, or rapid-response emergency spill containment.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single shutdown deep-clean to a fully embedded preventive-maintenance partnership — we'll match the schedule to your plant.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Annual shutdown deep cleans, post-installation cleans for new lines, emergency spill response, and pre-HSA-inspection cleans.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, shift-based, weekly, or monthly programmes delivered by a dedicated crew working safely around live production.",
          },
          {
            k: "Shutdown-Aligned Programmes",
            v: "Scheduled cleaning aligned to your preventive-maintenance and shutdown calendar — protecting asset uptime and minimising production loss.",
          },
        ],
      },
      {
        heading: "Why Plant & Facility Managers Trust Us",
        body: "We align our operations with HSA standards, your LOTO procedures, and your audit calendar from day one.",
        items: [
          {
            k: "Rigorous RAMS & Risk Mitigation",
            v: "No operative enters your floor without site-specific Risk Assessments and Method Statements (RAMS) engineered to address heavy machinery hazards, lock-out/tag-out (LOTO) procedures, and tight workspaces.",
          },
          {
            k: "Certified Technical Personnel",
            v: "Our heavy industrial crews hold comprehensive certifications including SafePass, CSCS, and IPAF/MEWP licences for operating high-access mobile platforms safely.",
          },
          {
            k: "Uncompromising Legislative Compliance",
            v: "We align all cleaning operations with the latest Health and Safety Authority (HSA) standards, ensuring your plant remains fully audit-ready for official inspections.",
          },
          {
            k: "Robust Commercial Insurance Cover",
            v: "Fully backed by high-limit Public and Employers' Liability Insurance tailored specifically to mitigate risks in high-hazard industrial environments.",
          },
        ],
      },
    ],
  },
  {
    slug: "warehouse",
    tag: "03 / Warehouse",
    title: "Warehouse & Distribution Centre Cleaning",
    body: "Professional cleaning services for warehouses, logistics hubs, and distribution centres.",
    img: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85",
    intro:
      "Logistics and distribution networks operate around the clock — and so do we. At AGS Cleaning Services, we specialise in heavy-duty warehouse cleaning that seamlessly integrates with live 24/7 pick-and-pack operations. Our industrial crews are trained to work safely alongside automated machinery, forklifts, and warehouse personnel — helping maintain clean, safe, and efficient warehouse operations.",
    features: [
      "Heavy-Duty Scrubber-Dryer Floor Care (ride-on & walk-behind)",
      "High-Bay Racking & Structural Dust Extraction",
      "Loading Bay & Dispatch Degreasing",
      "Integrated Office & Welfare Area Support",
      "External Yard Sweeping & Pressure Washing",
      "Available as One-Off cleans or 24/7 recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Our warehouse programmes flex to your operation — from a single shutdown deep-clean to a 24/7 embedded crew working live aisles around your pickers.",
        items: [
          {
            k: "Heavy-Duty Scrubber-Dryer Floor Care",
            v: "Deep mechanical scrubbing using advanced ride-on and walk-behind scrubber-dryers to remove heavy tyre marks, grease, and oil patches — helping maintain clean and safe floor surfaces.",
          },
          {
            k: "High-Bay Racking & Structural Dust Extraction",
            v: "Specialised high-level dust and debris removal from industrial racking systems, pallets, support beams, and light fixtures using safe reach equipment.",
          },
          {
            k: "Loading Bay & Dispatch Degreasing",
            v: "Intensive cleaning and degreasing of loading docks, bay doors, and levellers to maintain safety and prevent outdoor contamination from tracking into your facility.",
          },
          {
            k: "Integrated Office & Welfare Area Support",
            v: "Unified cleaning that covers the entire facility footprint, including warehouse floors, staff locker rooms, canteens, and transport offices.",
          },
          {
            k: "External Yard Sweeping & Pressure Washing",
            v: "Comprehensive exterior maintenance including yard sweeping, waste debris clearing, and high-pressure washing of loading bays and concrete aprons.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "Whether you need a single shutdown deep-clean or an ongoing embedded programme, we'll match the schedule to your dispatch operations.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Annual shutdown deep cleans, post-rack-install cleans, pre-inspection deep cleans — booked once, delivered to spec.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, weekly, or shift-based recurring programmes with a dedicated crew working safely around live pick-and-pack activity.",
          },
          {
            k: "24/7 Embedded Crews",
            v: "For high-throughput distribution centres, we deploy continuous crews scheduled around your operational requirements to minimise disruption.",
          },
        ],
      },
      {
        heading: "Why Logistics & Facility Managers Trust Us",
        body: "We plug into your existing H&S, programme, and supply-chain audit framework.",
        items: [
          {
            k: "Strict H&S and RAMS Compliance",
            v: "Operations are strictly governed by project-specific Risk Assessments and Method Statements (RAMS), focusing on segregation from moving warehouse machinery.",
          },
          {
            k: "Trained Warehouse Cleaning Personnel",
            v: "Our operators hold valid CSCS / MEWP / IPAF licences for high-level platforms and are fully trained in operating heavy ride-on industrial machinery.",
          },
          {
            k: "Documented Cleaning Procedures",
            v: "We provide documented cleaning services that support your site's hygiene and operational requirements.",
          },
          {
            k: "Rapid Spill Response & Flexibility",
            v: "Flexible deployment schedules tailored around your peak dispatch hours, helping minimise disruption to your daily operations.",
          },
        ],
      },
    ],
  },
  {
    slug: "food-factory",
    tag: "04 / Food Production",
    title: "Food Production & Beverage Facility Cleaning",
    body: "Professional cleaning services supporting food production hygiene and HACCP procedures.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/0gj56wf0_fsdfsdf.jpg",
    intro:
      "In food and beverage production, hygiene failure is not an option. At AGS Cleaning Services, we deliver high-specification sanitation services designed to withstand the most rigorous FSAI (Food Safety Authority of Ireland), BRCGS, and retail supplier audits. Every food-grade chemical we use, every method statement we draft, and every traceable log we sign is built to seamlessly integrate with your facility's HACCP plan — ensuring your lines are biologically safe, allergen-controlled, and ready for immediate operational sign-off.",
    features: [
      "Production Equipment & Machinery Cleaning",
      "Allergen-Aware & Food-Contact Safe Chemistry",
      "Pest-Conscious Cleaning & Deep Hygiene Protocols",
      "Available as One-Off shutdown cleans or recurring 365-day contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is engineered around your HACCP plan, your allergen matrix, and your retailer-audit calendar.",
        items: [
          {
            k: "Production Equipment & Machinery Cleaning",
            v: "Intensive external chemical sanitisation of complex production machinery, conveyor belts, filling lines, and support for Clean-in-Place (CIP) / Sterilise-in-Place (SIP) protocols.",
          },
          {
            k: "Allergen-Aware & Food-Contact Safe Chemistry",
            v: "Exclusive deployment of certified food-safe chemicals, with rigorous cross-contamination controls and colour-coded equipment to manage allergens (gluten, nuts, dairy, etc.) safely.",
          },
          {
            k: "Pest-Conscious Cleaning & Deep Hygiene Protocols",
            v: "Thorough deep cleaning of hidden debris traps, ingredient silos, and drain systems to eliminate harborage points — supporting your site's Pest Control framework.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single shutdown sanitation programme to a fully embedded 365-day team — we'll match the schedule to your line-clearance and audit cycle.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Annual shutdown deep-sanitation, post-installation cleans for new lines, pre-inspection deep cleans, and rapid-response containment cleans.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, shift-based, weekly, or batch-changeover programmes delivered by a dedicated food-hygiene-trained crew working around live production.",
          },
          {
            k: "365-Day Embedded Crews",
            v: "Continuous, integrated hygiene partners running night-shifts, weekends, and planned shutdowns — designed for high-velocity food and beverage plants.",
          },
        ],
      },
      {
        heading: "Why Food & Beverage Manufacturers Trust Us",
        body: "We slot directly into your HACCP plan, your retailer-audit framework, and your line-clearance procedures.",
        items: [
          {
            k: "Documented Cleaning Procedures",
            v: "We provide the audit-ready paper trail and Method Statements (RAMS) required to support compliance during food safety inspections.",
          },
          {
            k: "Specialised Food Safety Training",
            v: "Our operatives are heavily trained in food hygiene regulations, chemical handling (COSHH), allergen management, and cross-contamination prevention.",
          },
          {
            k: "Comprehensive Public & Employers' Liability Insurance",
            v: "Fully covered by comprehensive Public and Employers' Liability Insurance extended to cover specialised industrial food production environments.",
          },
        ],
      },
    ],
  },
  {
    slug: "cleanroom",
    tag: "05 / Cleanroom",
    title: "Cleanroom & Laboratory Cleaning",
    body: "Professional cleaning services for cleanrooms, laboratory suites, and controlled production environments.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/fs5fvz5z_a89e6c28dca64c8d3eaaae321c57b6ad.jpg",
    intro:
      "At AGS Cleaning Services, we provide professional cleaning services designed for cleanrooms, laboratory suites, and controlled production environments. Our technicians are trained to follow site-specific PPE and entry procedures, and to work with professional cleaning products and low-lint materials where required — helping maintain a clean, tidy, and controlled working environment for your team.",
    features: [
      "Documented Cleaning Procedures",
      "Professional Cleaning Products & Equipment",
      "Site-Specific PPE Procedures",
      "Professional Chemical Management",
      "Cleaning Records & Documentation",
      "Available as One-Off deep cleans or recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is designed around your facility's operating procedures, site access rules, and cleaning schedule.",
        items: [
          {
            k: "Documented Cleaning Procedures",
            v: "Cleaning executed to written procedures agreed with your team, so every visit follows the same consistent method.",
          },
          {
            k: "Professional Cleaning Products & Low-Lint Materials",
            v: "Use of professional cleaning products, low-lint wipes and mops where required for controlled environments.",
          },
          {
            k: "Site-Specific PPE & Entry Procedures",
            v: "Our staff follow your site's PPE requirements and entry procedures for controlled and restricted areas.",
          },
          {
            k: "Professional Chemical Management",
            v: "Careful selection and handling of cleaning chemicals with on-site SDS documentation available on request.",
          },
          {
            k: "Cleaning Records & Documentation",
            v: "Simple, transparent cleaning logs and sign-off sheets so you always have a record of the work completed.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single deep clean for a new or refurbished suite to an ongoing scheduled programme — we'll match the schedule to your operations.",
        items: [
          {
            k: "One-Off Deep Cleans",
            v: "Deep cleans for new or refurbished cleanrooms and laboratory suites, post-shutdown cleans, and pre-inspection deep cleans.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, shift-based, or weekly programmes delivered by a dedicated, site-trained crew aligned to your cleaning schedule.",
          },
        ],
      },
      {
        heading: "Why Cleanroom & Lab Teams Choose AGS",
        body: "We work closely with your facilities team and follow the procedures that apply to your site.",
        items: [
          {
            k: "Trained Cleaning Personnel",
            v: "Our operatives are trained to work within controlled environments, following site-specific procedures and PPE requirements.",
          },
          {
            k: "Site Supervisor Oversight",
            v: "A dedicated supervisor coordinates the team, checks quality, and stays in direct contact with your facilities lead.",
          },
          {
            k: "Documented Cleaning Procedures",
            v: "We provide written cleaning procedures and simple audit-friendly records so you always know what has been done and when.",
          },
          {
            k: "Comprehensive Public & Employers' Liability Insurance",
            v: "Backed by comprehensive Public and Employers' Liability Insurance — documentation available on request.",
          },
        ],
      },
    ],
  },
  {
    slug: "hospital",
    tag: "06 / Healthcare",
    title: "Hospital & Healthcare Environmental Services (EVS)",
    body: "Professional healthcare cleaning services supporting hygiene and infection prevention in hospitals, clinics, and medical facilities.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/vt9hnd9v_d1fd11ed6d37785fe178aa8ac0ce0e4c.jpg",
    intro:
      "At AGS Cleaning Services, we support hospitals, specialised day clinics, diagnostic hubs, and GP surgeries across Ireland. We provide professional cleaning services for healthcare environments, following site-specific procedures, appropriate PPE requirements, and documented cleaning methods.",
    features: [
      "Rigorous Colour-Coded Zone Discipline",
      "EN-Verified Technical Disinfection (EN 14476 / EN 13727)",
      "Specialist Terminal Cleaning for Isolation Rooms & Theatres",
      "Comprehensive Ward, Day-Room & Restroom Programmes",
      "Continuous High-Frequency Touchpoint Sanitisation",
      "Available as One-Off terminal cleans or 24/7 recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Designed around your facility's cleaning schedule and operational requirements.",
        items: [
          {
            k: "Rigorous Colour-Coded Zone Discipline",
            v: "Execution of cleaning schedules using a strict colour-coded equipment system (mops, cloths, machinery) to help reduce the risk of cross-contamination between clinical wards, restrooms, and public spaces.",
          },
          {
            k: "EN-Verified Technical Disinfection",
            v: "Exclusive use of EN 14476 (virucidal) and EN 13727 (bactericidal) medical-grade disinfectants to support effective disinfection procedures.",
          },
          {
            k: "Specialist Terminal Cleaning for Isolation Rooms & Theatres",
            v: "Deep, high-precision terminal cleaning of operating theatres, diagnostic rooms, and isolation zones following patient discharge.",
          },
          {
            k: "Comprehensive Ward, Day-Room & Restroom Programmes",
            v: "Structured, scheduled maintenance of patient-facing spaces, waiting areas, day-rooms, and clinical washrooms to sustain a hygienic and reassuring environment.",
          },
          {
            k: "Continuous High-Frequency Touchpoint Sanitisation",
            v: "Proactive, micro-particle sanitisation of high-frequency touchpoints (handrails, bed frames, door handles, light switches) executed discreetly during peak operational hours.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single terminal clean for an isolation room to a fully embedded 24/7 EVS team — we'll match the schedule to your clinical operations.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Terminal cleans following patient discharge, post-outbreak deep cleans, pre-inspection deep cleans, and theatre changeover cleans.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, shift-based, weekly, or ward-rotation programmes delivered by a dedicated clinically trained crew aligned to your IPC (Infection Prevention & Control) policy.",
          },
          {
            k: "24/7 Embedded EVS Teams",
            v: "Continuous, integrated environmental services partners running around-the-clock cover for hospitals, day clinics, and acute care facilities.",
          },
        ],
      },
      {
        heading: "Why Healthcare Facilities & Hospital Boards Trust Us",
        body: "We slot directly into your IPC framework, your HIQA evidence file, and your patient-flow operations.",
        items: [
          {
            k: "Documented Cleaning Procedures",
            v: "Our cleaning logs, method statements, and training matrices provide a transparent paper trail available on request.",
          },
          {
            k: "Healthcare Cleaning & Infection Control Training",
            v: "All healthcare operatives undergo training in blood-borne pathogen safety, sharps awareness, correct PPE donning/doffing, and clinical waste handling.",
          },
          {
            k: "Professionally trained and uniformed staff",
            v: "Our staff are professionally trained and uniformed, and are coached to work empathetically around vulnerable patients, residents, and active medical personnel.",
          },
          {
            k: "Comprehensive Public & Employers' Liability Insurance",
            v: "Backed by comprehensive Public and Employers' Liability Insurance — documentation available on request.",
          },
        ],
      },
    ],
  },
  {
    slug: "medical-device",
    tag: "07 / Medical Devices",
    title: "Medical Device Manufacturing & Assembly Area Cleaning",
    body: "Professional cleaning services for medical device manufacturing, assembly areas, and controlled production environments.",
    img: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?crop=entropy&cs=srgb&fm=jpg&q=85",
    intro:
      "At AGS Cleaning Services, we provide professional cleaning services for medical device manufacturing, assembly areas, and controlled production environments. Our operatives are trained to follow site-specific procedures, PPE requirements, and documented cleaning methods — supporting your team with reliable, well-documented cleaning services.",
    features: [
      "Production Area Cleaning",
      "Low-Lint Cleaning Materials (where required)",
      "Site-Specific PPE Procedures",
      "Documented Cleaning Records",
      "Available as One-Off deep cleans or recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is designed around your facility's operating procedures and cleaning schedule.",
        items: [
          {
            k: "Production Area Cleaning",
            v: "Cleaning of production equipment and assembly areas, high-value tooling fixtures, and protective enclosures without disrupting sensitive equipment.",
          },
          {
            k: "Documented Cleaning Procedures",
            v: "Cleaning executed to written procedures agreed with your team, so every visit follows the same consistent method.",
          },
          {
            k: "Low-Lint Cleaning Materials (where required)",
            v: "Use of low-lint wipes and appropriate cleaning materials where required for controlled production environments.",
          },
          {
            k: "Site-Specific PPE Procedures",
            v: "Our staff follow your site's PPE requirements and entry procedures for controlled and restricted areas.",
          },
          {
            k: "Documented Cleaning Records",
            v: "Simple, transparent cleaning logs and sign-off sheets so you always have a record of the work completed.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single deep clean for a new assembly suite to an ongoing scheduled programme — we'll match the schedule to your operations.",
        items: [
          {
            k: "One-Off Deep Cleans",
            v: "Deep cleans for new assembly lines, post-line-change deep cleans, and pre-inspection deep cleans.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily, shift-based, or weekly programmes delivered by a dedicated, site-trained crew aligned to your line-clearance schedule.",
          },
          {
            k: "Production Support Cleaning",
            v: "Scheduled cleaning cycles aligned to your production campaigns and product changeovers.",
          },
        ],
      },
      {
        heading: "Why Medical Device Manufacturers Choose AGS",
        body: "We work closely with your facilities team and follow the procedures that apply to your site.",
        items: [
          {
            k: "Documented Cleaning Procedures",
            v: "We provide written cleaning procedures and audit-friendly records so you always know what has been done and when.",
          },
          {
            k: "Trained Cleaning Personnel",
            v: "Our operators are trained to work within controlled production environments, following site-specific procedures and PPE requirements.",
          },
          {
            k: "Flexible Scheduling Around Production",
            v: "We coordinate shifts around your production schedules — executing cleaning during scheduled line maintenance or out-of-hours windows.",
          },
          {
            k: "Comprehensive Public & Employers' Liability Insurance",
            v: "Backed by comprehensive Public and Employers' Liability Insurance — documentation available on request.",
          },
        ],
      },
    ],
  },
  {
    slug: "retail-leisure",
    tag: "08 / Retail",
    title: "Retail, Leisure & Shopping Centre Cleaning",
    body: "High-standard environmental cleaning engineered for high-footfall shopping centres, premium showrooms, flagship retail stores, and leisure venues.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/xx16ux7g_10c86574dcf8bca2706dba9fd0f942a3.jpg",
    intro:
      "In the retail and leisure sectors, first impressions play an important role in customer experience and brand image. At AGS Cleaning Services, we deliver high-visibility cleaning solutions designed to keep shopping centres, retail stores, automotive showrooms, and fitness venues consistently clean and welcoming. Our dual focus combines meticulous out-of-hours deep cleaning with daytime janitorial presence — blending seamlessly into your customer-facing environment, protecting your brand image, helping maintain a clean and safe environment, and keeping your facility consistently clean from opening time to weekend rush hours.",
    features: [
      "Pre-Opening Daily Cleaning & Restocking",
      "Shopping Centre Common Area & Atrium Maintenance",
      "Shopfront Glazing & Architectural Glass Detailing",
      "High-Sanitation Gym, Changing-Room & Locker Support",
      "POS, High-Traffic Floors & Rapid Spill Response",
      "Flexible 24/7 Out-of-Hours & Event Scheduling",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is engineered around your trading hours, your customer flow, and your brand standards.",
        items: [
          {
            k: "Pre-Opening Daily Cleaning & Restocking",
            v: "Comprehensive early-morning or overnight cleaning of trading floors, entrance zones, and public spaces, combined with a full replenishment of washroom consumables before your doors open.",
          },
          {
            k: "Shopping Centre Common Area & Atrium Maintenance",
            v: "High-standard maintenance of expansive mall walkways, atriums, food courts, escalators, and lift cars to sustain a prestigious shopping environment.",
          },
          {
            k: "Shopfront Glazing & Architectural Glass Detailing",
            v: "Precision window cleaning and structural glass polishing to eliminate fingerprints, smudges, and weather marks — ensuring your storefronts look perfectly transparent.",
          },
          {
            k: "High-Sanitation Gym, Changing-Room & Locker Support",
            v: "Professional Cleaning & Disinfection of leisure facilities, focusing on high-contact gym equipment, locker rooms, saunas, and showers to help reduce the risk of cross-contamination.",
          },
          {
            k: "POS, High-Traffic Floors & Rapid Spill Response",
            v: "Continuous daytime attention to point-of-sale (POS) areas, payment terminals, and high-traffic flooring, paired with rapid spill response to mitigate slip hazards.",
          },
          {
            k: "Flexible 24/7 Out-of-Hours & Event Scheduling",
            v: "Fully customisable service delivery patterns that adapt to seasonal trading shifts, holiday peak weeks, late-night openings, and special promotional events.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a one-off post-event recovery clean to a fully embedded 24/7 retail team — we'll match the schedule to your trading calendar.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Pre-launch / store-opening deep cleans, post-event and post-sale recovery cleans, end-of-tenancy showroom cleans, and seasonal deep-clean refreshes.",
          },
          {
            k: "Recurring Contracts",
            v: "Daily pre-opening, evening or weekend programmes delivered by a dedicated, customer-facing crew aligned to your trading hours.",
          },
          {
            k: "Daytime Janitorial + Out-of-Hours Deep Clean",
            v: "Hybrid programme combining a visible daytime janitor presence with overnight deep cleans — ideal for shopping centres and flagship stores.",
          },
        ],
      },
      {
        heading: "Why Asset Managers & Retail Operations Trust Us",
        body: "We slot directly into your SLAs, your public-liability framework, and your brand-standards documentation.",
        items: [
          {
            k: "Customer-Centric Professionalism",
            v: "Our personnel are fully background-vetted, smartly uniformed, and highly coached in customer-service etiquette — acting as helpful, reassuring brand ambassadors while working in public-facing spaces.",
          },
          {
            k: "Proactive Slip & Fall Risk Mitigation",
            v: "We utilise strict colour-coded warning systems and state-of-the-art scrubber-dryers that leave floors bone-dry — directly helping facilities minimise public liability claims.",
          },
          {
            k: "Flawless Quality Audits & SLAs",
            v: "Operations are tracked against strict Service Level Agreements (SLAs) with regular management inspections to guarantee retail standards never drop.",
          },
          {
            k: "Comprehensive Liability Insurance Cover",
            v: "Backed by comprehensive Public and Employers' Liability Insurance fully tailored to protect active, high-traffic commercial environments.",
          },
        ],
      },
    ],
  },
  {
    slug: "post-construction",
    tag: "09 / Handover",
    title: "Post-Construction & Builders Cleaning",
    body: "Professional post-construction cleaning to prepare buildings for client handover.",
    img: "/services/post-construction.jpg",
    intro:
      "At AGS Cleaning Services, we understand that the final handover is the most critical phase of any construction project. Working closely with General Contractors and Site Managers, our highly trained post-construction crews deliver rapid, professional, and high-quality results under tight snag-list deadlines. From the initial rough clean to the final practical completion handover, we provide a seamless service delivered to high professional standards of presentation and safety.",
    features: [
      "Builders Clean (Phase 1) — construction dust, debris, plaster spots & material residues removed across all surfaces",
      "Sparkle Clean (Phase 2) — high-end detailed cleaning, fixture polishing, deep floor care & window scraping for showroom finish",
      "Heavy-Adhesive Removal — expert elimination of protective stickers, paint-spots, silicone & glue residues using appropriate cleaning methods for each surface",
      "High-Level Cleaning — safe dust and grime extraction from pipes, vent grilles, soffits & complex light fittings",
      "Responsive Snag-List Support — flexible rapid-return visits to resolve issues before the client's final walkthrough",
    ],
    sections: [
      {
        heading: "Our Multi-Stage Handover Process",
        body: "We deliver post-construction cleaning in distinct, contractor-friendly phases so your programme keeps moving without compromise on the final finish.",
        items: [
          {
            k: "Builders Clean (Phase 1)",
            v: "Comprehensive removal of construction dust, builders' debris, plaster spots, and material residues across all surfaces.",
          },
          {
            k: "Sparkle Clean (Phase 2)",
            v: "High-end detailed cleaning, polishing of fixtures, deep floor care, and window scraping to give the property its final showroom look.",
          },
          {
            k: "Heavy-Adhesive Removal",
            v: "Expert elimination of protective stickers, heavy paint-spots, silicone, and glue residues using appropriate cleaning methods for each surface.",
          },
          {
            k: "High-Level Cleaning",
            v: "Safe dust and grime extraction from high-level pipes, vent grilles, soffits, and complex light fittings.",
          },
          {
            k: "Responsive Snag-List Support",
            v: "Flexible, rapid-return visits to resolve unexpected snagging issues before the client's final walkthrough.",
          },
        ],
      },
      {
        heading: "Why Tier-1 Contractors Choose Us",
        body: "Main contractors choose AGS because we plug straight into your existing H&S, programme, and quality framework — no induction overhead, no compliance gaps.",
        items: [
          {
            k: "100% SafePass & CSCS Compliant",
            v: "Every team member is fully safety-trained, holds an active SafePass card, and strictly adheres to on-site PPE regulations.",
          },
          {
            k: "Full H&S Documentation",
            v: "Comprehensive Method Statements and Risk Assessments (RAMS) provided before stepping foot on any site.",
          },
          {
            k: "Comprehensive Liability Cover",
            v: "Fully backed by Public and Employers' Liability Insurance tailored for high-scale commercial construction.",
          },
        ],
      },
    ],
  },
  {
    slug: "school-university",
    tag: "10 / Education",
    title: "School, College & University Campus Cleaning",
    body: "Professional cleaning services for schools, colleges, and university campuses across Ireland.",
    img: "https://customer-assets.emergentagent.com/job_industrial-clean-ags/artifacts/1x7up5ez_8dbe9a62f1456973a1259e4379700a05.jpg",
    intro:
      "Educational environments require consistent cleaning standards, hygiene, and reliable service delivery. At AGS Cleaning Services, we deliver bespoke, comprehensive cleaning solutions tailored to the unique rhythms of academic schedules. From local primary schools to sprawling multi-building university campuses, our operations are built around student and staff safety — helping maintain clean and hygienic learning environments and working in line with school health and safety requirements and site procedures.",
    features: [
      "Vetted and professionally trained operatives",
      "Term-Time Maintenance & Out-of-Term Deep Cleans",
      "Classroom, Library, Lecture Theatre & Laboratory Care",
      "Canteen, Refectory & Washroom Hygiene Frameworks",
      "Gym Hall, Locker Room & Sports Facility Disinfection",
      "Available as One-Off summer-break deep cleans or recurring contracts",
    ],
    sections: [
      {
        heading: "What This Service Includes",
        body: "Every contract is engineered around your academic calendar, your safeguarding policy, and your facility footprint.",
        items: [
          {
            k: "Staff trained to work in educational environments",
            v: "Every member of our cleaning crew undergoes rigorous Garda Vetting and appropriate child protection training before being deployed to any campus.",
          },
          {
            k: "Term-Time Maintenance & Out-of-Term Deep Cleans",
            v: "Seamless daily cleaning during active academic terms, paired with high-intensity deep cleaning cycles, carpet extraction, and floor buffing during summer and mid-term breaks.",
          },
          {
            k: "Classroom, Library, Lecture Theatre & Laboratory Care",
            v: "Meticulous dusting, vacuuming, and sanitisation of student desks, common areas, research libraries, and high-frequency touchpoints in specialised science laboratories.",
          },
          {
            k: "Canteen, Refectory & Washroom Hygiene Frameworks",
            v: "High-level sanitisation of food preparation and dining spaces, combined with intensive, scheduled disinfection of toilet blocks to maintain optimal sanitary conditions.",
          },
          {
            k: "Gym Hall, Locker Room & Sports Facility Disinfection",
            v: "Specialised, heavy-duty cleaning and hygiene maintenance of sports flooring, indoor halls, changing rooms, and shower areas to combat odour and bacterial buildup.",
          },
        ],
      },
      {
        heading: "Choose Your Cleaning Frequency",
        body: "From a single summer-break deep clean to a fully embedded term-time team — we'll match the schedule to your academic calendar.",
        items: [
          {
            k: "One-Off Cleans",
            v: "Summer-break deep cleans, mid-term reset cleans, post-exam clean-ups, and pre-Open-Day showcase cleans.",
          },
          {
            k: "Recurring Term-Time Contracts",
            v: "Daily early-morning or evening programmes delivered by a dedicated Garda-vetted crew aligned to your timetable.",
          },
          {
            k: "Term-Time + Holiday Deep-Clean Cycles",
            v: "Hybrid programme combining daily term-time maintenance with intensive deep-clean blitzes during every school holiday window.",
          },
        ],
      },
      {
        heading: "Why Boards of Management & Procurement Officers Trust Us",
        body: "We slot directly into your child-protection policy, your HSA framework, and your procurement & SLA requirements.",
        items: [
          {
            k: "Flawless Legislative Compliance",
            v: "Our services operate in strict accordance with Health and Safety Authority (HSA) guidelines, backed by comprehensive, site-specific Method Statements and Risk Assessments (RAMS).",
          },
          {
            k: "Cross-Contamination & Absence Reduction",
            v: "We deploy advanced, colour-coded microfiber systems and certified eco-friendly disinfectants specifically targeted to lower student absenteeism caused by winter bugs and viruses.",
          },
          {
            k: "Flexible 24/7 Scheduling",
            v: "We design our cleaning windows around your timetables — offering early-morning, late-evening, or weekend shifts to guarantee zero disruption to exams, lectures, or extracurricular events.",
          },
          {
            k: "Comprehensive Public & Employers' Liability",
            v: "Fully backed by high-limit, robust commercial insurance options — providing total peace of mind for public educational assets and high-traffic student zones.",
          },
        ],
      },
    ],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);

// Maps a service slug to a site_type label compatible with the quote form dropdown
export const SLUG_TO_SITE_TYPE = {
  "commercial-office": "Commercial Office",
  "industrial": "Industrial",
  "warehouse": "Warehouse",
  "food-factory": "Food Factory",
  "cleanroom": "Cleanroom",
  "hospital": "Hospital",
  "medical-device": "Medical Device",
  "retail-leisure": "Retail / Leisure / Shopping Centre",
  "post-construction": "Post-Construction",
  "school-university": "School / University",
};
